import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  InputAdornment,
} from "@mui/material";
import { Page, PageHeader } from "../../components/layout/Page";
import { useQuery } from "@tanstack/react-query";
import { fetchInkPrices } from "../../api/priceTables";
import { useAdminCrud } from "../../hooks/useAdminCrud";
import { useDebounce } from "../../hooks/useDebounce";
import {
  ActionsHeadCell,
  ConfirmDeleteDialog,
  DataCard,
  DataToolbar,
  EditRowDialog,
  RefreshAndAdd,
  RowActions,
  SearchField,
  TableMessageRow,
  TablePager,
} from "./DataTableParts";
import { FULL_SPAN_SX, TIGHT_COL_SX, formatMoney } from "./dataTableStyles";
import { useRowDialogs } from "./useRowDialogs";
import { queryKeys } from "../../api/queryKeys";

interface InkPrice {
  id: number;
  brand: string;
  model: string;
  normalized_model: string;
  price_black: number | null;
  price_cyan: number | null;
  price_magenta: number | null;
  price_yellow: number | null;
  price_tricolor: number | null;
  source_file: string | null;
  created_at: string;
  updated_at: string;
}

interface InkPriceForm {
  brand: string;
  model: string;
  price_black: string;
  price_cyan: string;
  price_magenta: string;
  price_yellow: string;
  price_tricolor: string;
}

const emptyForm: InkPriceForm = {
  brand: "",
  model: "",
  price_black: "",
  price_cyan: "",
  price_magenta: "",
  price_yellow: "",
  price_tricolor: "",
};

const BRANDS = ["HP", "CANON", "BROTHER", "EPSON", "OKI", "OTHER"];

// One right-aligned column per cartridge colour, so prices line up and scan down the page.
const PRICE_COLUMNS: {
  key: "price_black" | "price_cyan" | "price_magenta" | "price_yellow" | "price_tricolor";
  label: string;
  swatch: string;
}[] = [
  { key: "price_black", label: "Black", swatch: "#1f2937" },
  { key: "price_cyan", label: "Cyan", swatch: "#00a3d9" },
  { key: "price_magenta", label: "Magenta", swatch: "#d0258f" },
  { key: "price_yellow", label: "Yellow", swatch: "#f2c200" },
  { key: "price_tricolor", label: "Tri-colour", swatch: "conic-gradient(#00a3d9 0 120deg, #d0258f 0 240deg, #f2c200 0)" },
];
// Brand, Model, the price columns, Source, Updated, Actions
const COL_COUNT = 2 + PRICE_COLUMNS.length + 3;

function InkSwatch({ colour }: { colour: string }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: colour, flexShrink: 0 }}
    />
  );
}

const toForm = (item: InkPrice): InkPriceForm => ({
  brand: item.brand,
  model: item.model,
  price_black: item.price_black?.toString() || "",
  price_cyan: item.price_cyan?.toString() || "",
  price_magenta: item.price_magenta?.toString() || "",
  price_yellow: item.price_yellow?.toString() || "",
  price_tricolor: item.price_tricolor?.toString() || "",
});

/** One model: brand, model, a price per colour, source and date. */
function InkPriceRow({ item, onEdit, onDelete }: { item: InkPrice; onEdit: () => void; onDelete: () => void }) {
  return (
    <TableRow hover>
      <TableCell sx={TIGHT_COL_SX}>
        <Chip label={item.brand} size="small" variant="outlined" />
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Typography variant="body2" fontWeight={600}>
          {item.model}
        </Typography>
      </TableCell>
      {PRICE_COLUMNS.map((col) => {
        const price = item[col.key];
        return (
          <TableCell key={col.key} align="right" sx={TIGHT_COL_SX}>
            {price ? (
              formatMoney(price)
            ) : (
              <Box component="span" sx={{ color: "text.disabled" }}>-</Box>
            )}
          </TableCell>
        );
      })}
      <TableCell>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 360 }}>
          {item.source_file || "Manual Entry"}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...TIGHT_COL_SX, color: "text.secondary" }}>
        {new Date(item.updated_at).toLocaleDateString()}
      </TableCell>
      <RowActions
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </TableRow>
  );
}

export default function InkDatabaseManagement() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState(""); // Local input state for immediate feedback
  const [brandFilter, setBrandFilter] = useState("");
  const { editDialog, deleteDialog, formData, setFormData, isAddMode, openEdit: handleOpenEditDialog, closeEdit: handleCloseDialog, openDelete, closeDelete } =
    useRowDialogs<InkPrice, InkPriceForm>(emptyForm, toForm);

  // Debounce the search query to avoid refetching on every keystroke
  const debouncedSearchQuery = useDebounce(searchInput, 300);

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery, brandFilter]);

  // Fetch ink prices
  const {
    data: inkPricesData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.inkPrices.list(page, rowsPerPage, debouncedSearchQuery, brandFilter),
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (debouncedSearchQuery) params.append("search", debouncedSearchQuery);
      if (brandFilter) params.append("brand", brandFilter);
      
      return fetchInkPrices(params);
    },
    staleTime: 30000,
    placeholderData: (previousData) => previousData, // Keep previous data while fetching
  });

  const { createMutation, updateMutation, deleteMutation } = useAdminCrud<Partial<InkPrice>>({
    endpoint: "/ink-prices",
    label: "ink price",
    invalidate: queryKeys.inkPrices.all,
    onSaved: () => handleCloseDialog(),
    onDeleted: () => closeDelete(),
  });

  const handleSave = () => {
    const data: Partial<InkPrice> = {
      brand: formData.brand,
      model: formData.model,
      price_black: formData.price_black ? parseFloat(formData.price_black) : null,
      price_cyan: formData.price_cyan ? parseFloat(formData.price_cyan) : null,
      price_magenta: formData.price_magenta ? parseFloat(formData.price_magenta) : null,
      price_yellow: formData.price_yellow ? parseFloat(formData.price_yellow) : null,
      price_tricolor: formData.price_tricolor ? parseFloat(formData.price_tricolor) : null,
    };

    if (isAddMode) {
      createMutation.mutate(data);
    } else if (editDialog.item) {
      updateMutation.mutate({ id: editDialog.item.id, data });
    }
  };

  const handleDelete = () => {
    if (deleteDialog.item) {
      deleteMutation.mutate(deleteDialog.item.id);
    }
  };

  const inkPrices = inkPricesData?.data || [];
  const totalCount = inkPricesData?.pagination?.total || 0;
  const showLoadingOverlay = isLoading && !inkPricesData; // Only show full loading on initial load

  const priceField = (col: (typeof PRICE_COLUMNS)[number], label: string) => (
    <TextField
      label={label}
      value={formData[col.key]}
      onChange={(e) => setFormData({ ...formData, [col.key]: e.target.value })}
      type="number"
      size="small"
      fullWidth
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <InkSwatch colour={col.swatch} />
            <Box component="span" sx={{ ml: 1 }}>$</Box>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Page width="full">
      <PageHeader
        title="Ink Price Database"
        subtitle="Manage ink and toner cartridge pricing data"
        actions={<RefreshAndAdd onRefresh={() => refetch()} onAdd={() => handleOpenEditDialog(null)} />}
      />
      <DataCard>
        <DataToolbar summary={inkPricesData ? `${totalCount.toLocaleString()} ${totalCount === 1 ? "model" : "models"}` : undefined}>
          <SearchField
            placeholder="Search by model..."
            value={searchInput}
            onChange={setSearchInput}
            busy={isFetching}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              value={brandFilter}
              label="Brand"
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <MenuItem value="">All Brands</MenuItem>
              {BRANDS.map((brand) => (
                <MenuItem key={brand} value={brand}>
                  {brand}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DataToolbar>

        {error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            Failed to load ink prices: {(error as Error).message}
          </Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 920 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={TIGHT_COL_SX}>Brand</TableCell>
                    <TableCell>Model</TableCell>
                    {PRICE_COLUMNS.map((col) => (
                      <TableCell key={col.key} align="right" sx={{ ...TIGHT_COL_SX, minWidth: { xs: 96, xl: 128 } }}>
                        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                          <InkSwatch colour={col.swatch} />
                          {col.label}
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell>Source</TableCell>
                    <TableCell sx={TIGHT_COL_SX}>Updated</TableCell>
                    <ActionsHeadCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {showLoadingOverlay ? (
                    <TableMessageRow colSpan={COL_COUNT} loading />
                  ) : inkPrices.length === 0 ? (
                    <TableMessageRow colSpan={COL_COUNT}>
                      No ink prices found. {searchInput || brandFilter ? "Try adjusting your search criteria." : "Click \"Add New\" to add your first entry."}
                    </TableMessageRow>
                  ) : (
                    inkPrices.map((item: InkPrice) => (
                      <InkPriceRow
                        key={item.id}
                        item={item}
                        onEdit={() => handleOpenEditDialog(item)}
                        onDelete={() => openDelete(item)}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePager count={totalCount} page={page} setPage={setPage} rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} />
          </>
        )}
      </DataCard>

      {/* Edit/Add Dialog */}
      <EditRowDialog
        open={editDialog.open}
        title={isAddMode ? "Add New Ink Price" : "Edit Ink Price"}
        onClose={handleCloseDialog}
        onSave={handleSave}
        saveDisabled={!formData.brand || !formData.model || createMutation.isPending || updateMutation.isPending}
        saving={createMutation.isPending || updateMutation.isPending}
      >
        <FormControl fullWidth size="small">
          <InputLabel>Brand</InputLabel>
          <Select
            value={formData.brand}
            label="Brand"
            onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
          >
            {BRANDS.map((brand) => (
              <MenuItem key={brand} value={brand}>
                {brand}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Model"
          value={formData.model}
          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
          fullWidth
          required
          size="small"
        />
        <Typography variant="subtitle2" color="text.secondary" sx={{ ...FULL_SPAN_SX, mt: 1, mb: -0.5 }}>
          Prices (leave empty if not applicable)
        </Typography>
        {priceField(PRICE_COLUMNS[0], "Black")}
        {priceField(PRICE_COLUMNS[1], "Cyan")}
        {priceField(PRICE_COLUMNS[2], "Magenta")}
        {priceField(PRICE_COLUMNS[3], "Yellow")}
        {priceField(PRICE_COLUMNS[4], "Tricolor")}
      </EditRowDialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog open={deleteDialog.open} onCancel={closeDelete} onConfirm={handleDelete} deleting={deleteMutation.isPending}>
        Are you sure you want to delete the ink price for{" "}
        <strong>{deleteDialog.item?.brand} {deleteDialog.item?.model}</strong>?
        This action cannot be undone.
      </ConfirmDeleteDialog>
    </Page>
  );
}
